import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, X, Home, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Lightweight "Add existing property to portfolio" modal.
 *
 * Reuses the existing DealCreate backend schema. The backend endpoint
 * ``POST /api/deals/manual`` computes metrics deterministically (no LLM
 * call) and inserts the record as ``is_saved: true`` so it appears in
 * My Portfolio immediately.
 */
export default function AddPropertyDialog({ open, onClose, onAdded }) {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    property_address: '',
    purchase_price: '',
    num_units: 1,
    annual_income: '',
    annual_property_tax: '',
    annual_insurance: 1200,
    monthly_maintenance: 150,
    down_payment_percent: 20,
    interest_rate: 5.5,
    loan_term_years: 25,
  });

  if (!open) return null;

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const num = (v, fallback = 0) => (v === '' || v === null || v === undefined ? fallback : Number(v));

  const canSubmit = form.property_address.trim().length > 0 && num(form.purchase_price) > 0;

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      const payload = {
        property_address: form.property_address.trim(),
        purchase_price: num(form.purchase_price),
        num_units: num(form.num_units, 1),
        annual_income: num(form.annual_income),
        annual_property_tax: num(form.annual_property_tax),
        down_payment_percent: num(form.down_payment_percent, 20),
        interest_rate: num(form.interest_rate, 5.5),
        loan_term_years: num(form.loan_term_years, 25),
        annual_insurance: num(form.annual_insurance, 1200),
        monthly_maintenance: num(form.monthly_maintenance, 150),
      };
      const resp = await axios.post(`${BACKEND_URL}/api/deals/manual`, payload, { withCredentials: true });
      toast.success(t('myPortfolio.manualAddSuccess'));
      onAdded?.(resp.data);
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('myPortfolio.manualAddFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="add-property-dialog"
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full sm:max-w-2xl bg-white dark:bg-[#131824] rounded-t-3xl sm:rounded-3xl shadow-2xl border border-slate-200 dark:border-slate-800 max-h-[92vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 flex items-center justify-between px-6 py-4 border-b border-slate-100 dark:border-slate-800 bg-white/95 dark:bg-[#131824]/95 backdrop-blur-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#45C04A] to-[#65D95A] flex items-center justify-center">
              <Home className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="font-primary font-bold text-[#12355B] dark:text-white">
                {t('myPortfolio.manualAdd')}
              </h2>
              <p className="text-[11px] text-slate-400 max-w-md">
                {t('myPortfolio.manualAddSubtitle')}
              </p>
            </div>
          </div>
          <button
            data-testid="add-property-close"
            onClick={onClose}
            className="p-2 rounded-lg text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label={t('myPortfolio.form.address')} full>
            <input
              data-testid="ap-address"
              type="text"
              value={form.property_address}
              onChange={(e) => set('property_address', e.target.value)}
              placeholder={t('myPortfolio.form.addressPlaceholder')}
              className="w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 dark:text-white px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#45C04A]/30"
            />
          </Field>

          <Field label={t('myPortfolio.form.purchasePrice')} prefix="$">
            <NumInput testId="ap-price" value={form.purchase_price} onChange={(v) => set('purchase_price', v)} />
          </Field>
          <Field label={t('myPortfolio.form.numUnits')}>
            <NumInput testId="ap-units" value={form.num_units} onChange={(v) => set('num_units', v)} />
          </Field>

          <Field label={t('myPortfolio.form.annualIncome')} prefix="$">
            <NumInput testId="ap-income" value={form.annual_income} onChange={(v) => set('annual_income', v)} />
          </Field>
          <Field label={t('myPortfolio.form.annualTax')} prefix="$">
            <NumInput testId="ap-tax" value={form.annual_property_tax} onChange={(v) => set('annual_property_tax', v)} />
          </Field>

          <Field label={t('myPortfolio.form.downPaymentPercent')} suffix="%">
            <NumInput testId="ap-dp" value={form.down_payment_percent} onChange={(v) => set('down_payment_percent', v)} />
          </Field>
          <Field label={t('myPortfolio.form.interestRate')} suffix="%">
            <NumInput testId="ap-rate" value={form.interest_rate} onChange={(v) => set('interest_rate', v)} />
          </Field>

          <Field label={t('myPortfolio.form.loanTerm')}>
            <NumInput testId="ap-term" value={form.loan_term_years} onChange={(v) => set('loan_term_years', v)} />
          </Field>
          <Field label={t('myPortfolio.form.annualInsurance')} prefix="$">
            <NumInput testId="ap-ins" value={form.annual_insurance} onChange={(v) => set('annual_insurance', v)} />
          </Field>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-100 dark:border-slate-800 bg-white/95 dark:bg-[#131824]/95 backdrop-blur-sm">
          <Button data-testid="add-property-cancel" variant="outline" onClick={onClose}>
            {t('myPortfolio.form.cancel')}
          </Button>
          <Button
            data-testid="add-property-submit"
            onClick={submit}
            disabled={!canSubmit || saving}
            className="bg-[#45C04A] hover:bg-[#65D95A] text-[#12355B] rounded-xl px-6 font-semibold disabled:opacity-50"
          >
            {saving ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t('myPortfolio.adding')}</>
            ) : (
              <><Plus className="w-4 h-4 mr-2" />{t('myPortfolio.manualAddCta')}</>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, prefix, suffix, full }) {
  return (
    <div className={full ? 'sm:col-span-2' : ''}>
      <label className="block text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1.5">
        {label}
      </label>
      <div className="relative">
        {prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400 pointer-events-none">{prefix}</span>}
        <div className={`${prefix ? '[&_input]:pl-7' : ''} ${suffix ? '[&_input]:pr-8' : ''}`}>
          {children}
        </div>
        {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-400 pointer-events-none">{suffix}</span>}
      </div>
    </div>
  );
}

function NumInput({ value, onChange, testId }) {
  return (
    <input
      data-testid={testId}
      type="number"
      inputMode="decimal"
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 dark:text-white px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#45C04A]/30"
    />
  );
}
